import { useNotification } from '@app/AppLayout/NotificationContext';
import { RemoveButton } from '@app/components/RemoveButton';
import { useEntity } from '@app/hooks/useEntity';
import { Facility } from '@app/model/Facility';
import { useCountryStore, useFacilityStore } from '@app/store/entityStore';
import { ActionList, ActionListItem, AlertVariant, Bullseye, Button, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, Icon, MenuToggle, MenuToggleElement, PageSection, Pagination, PaginationVariant, SearchInput, Select, SelectOption, Split, SplitItem, Title, Toolbar, ToolbarContent, ToolbarFilter, ToolbarGroup, ToolbarItem, ToolbarLabelGroup, ToolbarToggleGroup, Tooltip } from '@patternfly/react-core';
import { FilterIcon, PencilAltIcon, SearchIcon } from '@patternfly/react-icons';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useFilterRestore } from '@app/hooks/useFilterRestore';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { TypeaheadSelect } from '@patternfly/react-templates';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { DataLoadErrorRow } from '@app/components/DataLoadError';

const emptyFilters = (): Record<string, string[]> => ({ country: [], name: [] });

/** What comes back when the user returns from a facility's detail page. */
interface FacilitiesView {
  filters: Record<string, string[]>;
  category: string | number | undefined;
}

const FacilitiesOverview: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification()

  const {items: rows, isLoaded, error, retry} = useEntity(useFacilityStore);
  const remove = useFacilityStore(state => state.delete);
  const {items: countries} = useEntity(useCountryStore);

  // The filters last applied here, kept only for the trip out to a facility and back.
  const { restored, keep } = useFilterRestore<FacilitiesView>('facilities');
  const [filters, setFilters] = React.useState<Record<string, string[]>>(restored?.filters ?? emptyFilters());
  const [currentCategory, setCurrentCategory] = React.useState<string | number | undefined>(restored?.category ?? 'Name');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = React.useState(false);
  const [nameFilterInput, setNameFilterInput] = React.useState('');
  const { restoredPage, rememberRow, rowProps } = useRowRestore('facilities');
  const [page, setPage] = React.useState(restoredPage ?? 1);
  const { perPage, setPerPage } = useTablePreferences('facilities', 10);
  const columns = ['Name', 'Contact Person', 'Contact Email', 'Contact Phone', 'Location', 'Actions'];

  React.useEffect(() => {
    keep({ filters, category: currentCategory });
  }, [filters, currentCategory, keep]);

  // --- Derived State: Filtering & Pagination ---
  const filteredRows = React.useMemo<Facility[]>(() => {
    const nameFilters = filters.name || [];
    const countryFilters = filters.country || [];

    if (nameFilters.length === 0 && countryFilters.length === 0) return rows;

    return rows.filter((row: Facility) =>
      (nameFilters.length === 0 ||
        nameFilters.some((name) => row.name.toLowerCase().includes(name.toLowerCase()))) &&
      (countryFilters.length === 0 ||
        countryFilters.includes(`${row.address?.country?.flag} ${row.address?.country?.name}`))
    );
  }, [rows, filters]);

  const paginatedRows = React.useMemo<Facility[]>(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  // Filters changing can shrink the result set below the current page.
  // Skips the first run: on mount there is nothing to correct, and resetting here
  // would discard the page restored after coming back from a facility's detail.
  const isFirstFilterRun = React.useRef(true);
  React.useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    setPage(1);
  }, [filters]);

  const handleSetPage = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handlePerPageSelect = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPerPage: number,
    newPage: number
  ) => {
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const buildPagination = (variant: 'bottom' | 'top' | PaginationVariant, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={handleSetPage}
      onPerPageSelect={handlePerPageSelect}
      variant={variant}
      titles={{
        paginationAriaLabel: `${variant} pagination`
      }}
    />
  );

  const onDeleteFilter = (type: string | ToolbarLabelGroup, id: string) => {
    if (type === 'Country') {
      setFilters({
        ...filters,
        country: (filters.country || []).filter((fil: string) => fil !== id)
      });
    } else if (type === 'Name') {
      setFilters({
        ...filters,
        name: (filters.name || []).filter((fil: string) => fil !== id)
      });
    } else {
      setFilters(emptyFilters());
    }
  };

  const onRemoveConfirm = async (id: string | number) => {
    try {
      await remove(id);
      addNotification("Facility removed", AlertVariant.success, 'Facility removed with success');
    } catch (error: any){
      addNotification(error.message, AlertVariant.danger, "Error removing facility");
    }
  }

  const clearFilters = () => {
    setFilters(emptyFilters());
  };

  const onCategoryToggle = () => {
    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
  };

  const onCategorySelect = (event?: React.MouseEvent<Element, MouseEvent>, value?: string | number) => {
    setCurrentCategory(value);
    setIsCategoryDropdownOpen(false);
  };

  const onNameFilterInputChange = (newValue: string) => {
    setNameFilterInput(newValue);
  };

  const onNameFilterInput = () => {
    const prevFilters = filters.name;
    setFilters({ ...filters, name: ((prevFilters ||[]).includes(nameFilterInput) ? prevFilters : [...(prevFilters || []), nameFilterInput] ) || [] });
  };

  const onCountrySelect = (selection?: string | number) => {
    setFilters({ ...filters, country: selection === undefined ? [] : [String(selection)] });
  };

  const buildCategoryDropdown = () => {
    const categoryMenuItems = [
      <SelectOption key="cat1" value="Country">
        Country
      </SelectOption>,
      <SelectOption key="cat2" value="Name">
        Name
      </SelectOption>,
    ];

    return (
      <ToolbarItem>
        <Select
          onSelect={onCategorySelect}
          selected={currentCategory}
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle
              ref={toggleRef}
              onClick={onCategoryToggle}
              isExpanded={isCategoryDropdownOpen}
              icon={<FilterIcon />}
              style={
                {
                  width: '100%',
                  verticalAlign: 'text-bottom'
                } as React.CSSProperties
              }
            >
              {currentCategory}
            </MenuToggle>
          )}
          isOpen={isCategoryDropdownOpen}
          onOpenChange={(isOpen) => setIsCategoryDropdownOpen(isOpen)}
        >
          {categoryMenuItems}
        </Select>
      </ToolbarItem>
    );
  };

  const buildFilterDropdown = () => {
    const selectedCountry = filters.country?.[0];
    const countryOptions = countries.map((country) => {
      const label = country.flag + ' ' + country.name;
      return { content: label, value: label, selected: label === selectedCountry };
    });
  
    return (
      <React.Fragment>
        <ToolbarFilter
          labels={filters.country}
          deleteLabel={(category, chip) => onDeleteFilter(category, chip as string)}
          categoryName="Country"
          showToolbarItem={currentCategory === 'Country'}
        >
          <TypeaheadSelect
            /* Remount whenever the filter changes from the outside (label removed,
               "clear all filters") so the input text follows the chip. */
            key={selectedCountry ?? ''}
            id="facility-country-filter"
            aria-label="Country"
            initialOptions={countryOptions}
            placeholder="Filter by country..."
            noOptionsFoundMessage={(filter: string) => `No country was found for "${filter}"`}
            onSelect={(_event, value) => onCountrySelect(value)}
            onClearSelection={() => onCountrySelect(undefined)}
            toggleWidth="220px"
          />
        </ToolbarFilter>
        <ToolbarFilter
          labels={filters.name}
          deleteLabel={(category, chip) => onDeleteFilter(category, chip as string)}
          categoryName="Name"
          showToolbarItem={currentCategory === 'Name'}
        >
          <SearchInput
            aria-label="name filter"
            placeholder="Filter by name..."
            onChange={(_event, value) => onNameFilterInputChange(value)}
            value={nameFilterInput}
            onClear={() => {
              onNameFilterInputChange('');
            }}
            onSearch={onNameFilterInput}
          />
        </ToolbarFilter>
      </React.Fragment>
    );
  };

  const renderToolbar = () => (
    <Toolbar
      id="toolbar-with-chip-groups"
      clearAllFilters={clearFilters}
      collapseListedFiltersBreakpoint="xl"
    >
      <ToolbarContent>
        <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
          <ToolbarGroup
            variant="filter-group"
            style={
              {
                lineHeight: '22px',
                alignItems: 'center'
              } as React.CSSProperties
            }
          >
            {buildCategoryDropdown()}
            {buildFilterDropdown()}
          </ToolbarGroup>
        </ToolbarToggleGroup>
        <ToolbarItem variant="pagination">{buildPagination('top', true)}</ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const emptyState = (
    <EmptyState  headingLevel="h5" icon={SearchIcon}  titleText="Clear all filters and try again.">
      <EmptyStateBody>No results match the filter criteria. Clear all filters and try again.</EmptyStateBody>
      <EmptyStateFooter>
        <EmptyStateActions>
          <Button
            variant="link"
            onClick={() => {
              setFilters({ country: [], name: [], });
            }}
          >
            Clear all filters
          </Button>
        </EmptyStateActions>
      </EmptyStateFooter>
    </EmptyState>
  );

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper  isWidthLimited>
      <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Facilities</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <Tooltip content="Create a new facility">
                  <Button id="new-button" variant='primary' onClick={() => navigate('/facilities/new')}>
                    New Facility
                  </Button>
                </Tooltip>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>
      <PageSection isWidthLimited isFilled>
        {renderToolbar()}

        {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
            gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
        <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset))', minHeight: 0 }}>
          <InnerScrollContainer>
            <Table aria-label="Partners" isStickyHeader variant='compact' gridBreakPoint="">
              <Thead>
                <Tr>
                  <Th key={0}>{columns[0]}</Th>
                  <Th key={1}>{columns[1]}</Th>
                  <Th key={2}>{columns[2]}</Th>
                  <Th key={3}>{columns[3]}</Th>
                  <Th key={4}>{columns[4]}</Th>
                  <Th key={5} width={10} isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>{columns[5]}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row, rowIndex) => (
                    <Tr key={rowIndex} {...rowProps(row.id)}>
                    <>
                      <Td dataLabel={columns[0]}>{row.name}</Td>
                      <Td dataLabel={columns[1]}>{row.contactPerson}</Td>
                      <Td dataLabel={columns[2]}>{row.contactEmail}</Td>
                      <Td dataLabel={columns[3]}>{row.contactMobile}</Td>
                      <Td dataLabel={columns[4]}>
                        {row.address?.city}
                        {row.address?.city && row.address?.country ? '/' : ''} 
                        {row.address?.country?.name}</Td>
                      <Td dataLabel={columns[5]} modifier="nowrap"
                        isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>
                        <ActionList>
                          <ActionListItem>
                            <Tooltip content="Edit">
                              <Button icon={<Icon size='lg'>
                                  <PencilAltIcon />
                                </Icon>} variant="plain" id="edit-button" onClick={ () => { rememberRow(row.id, page); navigate('/facilities/' + row.id); } } />
                            </Tooltip>
                          </ActionListItem>
                          <ActionListItem>
                            <RemoveButton confirmationMessage='Confirm facility removal?' onRemoveConfirm={() => onRemoveConfirm(row.id)} />
                          </ActionListItem>
                        </ActionList>
                      </Td>
                    </>
                  </Tr>
                ))}
                {/* Nothing to show: distinguish "the load failed" from "no records match" */}
                {error && filteredRows.length === 0 && (
                  <DataLoadErrorRow colSpan={6} error={error} entityLabel="facilities" onRetry={retry} />
                )}
                {!error && isLoaded && filteredRows.length === 0 && (
                  <Tr>
                    <Td colSpan={6}>
                      <Bullseye>{emptyState}</Bullseye>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </InnerScrollContainer>
        </OuterScrollContainer>
        {buildPagination('bottom', false)}
      </PageSection>
    </React.Fragment>
  );
};

export { FacilitiesOverview as Facilities };
